import { useCallback, useState } from 'react'
import { router } from '@inertiajs/react'
import { Background, BackgroundVariant, Controls, ReactFlow } from '@xyflow/react'
import { useWorkstationPageContext } from '@/frontend/features/AgentChats/workstationPageContext'
import OntologyGraphEdge, { type OntologyFlowEdge } from '@/frontend/features/Fde/OntologyGraphEdge'
import OntologyGraphInspector from '@/frontend/features/Fde/OntologyGraphInspector'
import OntologyGraphNode, { type OntologyFlowNode } from '@/frontend/features/Fde/OntologyGraphNode'
import OntologyNodeContextMenu from '@/frontend/features/Fde/OntologyNodeContextMenu'
import OntologyGraphToolbar from '@/frontend/features/Fde/OntologyGraphToolbar'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import useOntologyGraphView from '@/frontend/features/Fde/useOntologyGraphView'
import useOntologyHistory from '@/frontend/features/Fde/useOntologyHistory'
import type { DataAsset, DataBinding, Evidence, FdeCopy, ModelElement, ProcessMap, Relationship, SelectedProcess } from '@/frontend/features/Fde/types'

interface Props {
  projectId: string
  elements: ModelElement[]
  relationships: Relationship[]
  evidences: Evidence[]
  processMaps: ProcessMap[]
  selectedProcess: SelectedProcess | null
  dataAssets: DataAsset[]
  dataBindings: DataBinding[]
  copy: FdeCopy
  canManage: boolean
  initialFocusElementId?: string | null
  onOpenType: (classId: string) => void
  onEdit: (relationship: Relationship) => void
  onEditElement: (element: ModelElement) => void
}

const nodeTypes = { ontology: OntologyGraphNode }
const edgeTypes = { ontology: OntologyGraphEdge }

function processMaterialPath(projectId: string, processMap: ProcessMap) {
  if (processMap.element_id) return fdeProjectPath(fdeRoutes.workspace, projectId, {
    view: 'processes', process_element_id: processMap.element_id, process_mode: 'reported',
  })
  const spoken = processMap.modes.find((mode) => mode.mode === 'falado')
  const observed = processMap.modes.find((mode) => mode.mode === 'observado')
  const selected = spoken || observed || processMap.modes[0]
  return fdeProjectPath(fdeRoutes.material, projectId, { file: selected?.path })
}

export default function RelationshipGraph({ projectId, elements, relationships, evidences, processMaps, selectedProcess: loadedProcess, dataAssets, dataBindings, copy, canManage, initialFocusElementId, onOpenType, onEdit, onEditElement }: Props) {
  const graph = useOntologyGraphView({ elements, relationships, copy, initialFocusId: initialFocusElementId })
  const [contextMenu, setContextMenu] = useState<{ nodeId: string; x: number; y: number } | null>(null)
  const closeContextMenu = useCallback(() => setContextMenu(null), [])
  const history = useOntologyHistory(
    projectId,
    graph.selectedNode ? 'element' : graph.selectedRelationship ? 'relationship' : undefined,
    String(graph.selectedNode?.id || graph.selectedRelationship?.id || '') || undefined,
  )
  const contextElement = contextMenu ? elements.find((element) => String(element.id) === contextMenu.nodeId) ?? null : null
  const contextProcess = contextElement ? processMaps.find((process) => String(process.element_id) === String(contextElement.id)) ?? null : null
  const selectedProcess = graph.selectedNode ? processMaps.find((process) => String(process.element_id) === String(graph.selectedNode!.id)) ?? null : null
  const selectedProcessDetail = selectedProcess && String(loadedProcess?.summary.element_id) === String(selectedProcess.element_id) ? loadedProcess : null

  useWorkstationPageContext('ontology-client-map', {
    surface: 'forge',
    section: 'ontology',
    view: 'client_map',
    project: { id: projectId },
    filters: [{
      perspective: graph.perspective,
      depth: graph.depth,
      text_search: graph.query || undefined,
      gaps_only: graph.showGaps,
      anchors: graph.anchorElements.map((element) => ({ id: element.id, name: element.name })),
    }],
    selection: graph.selectedNode ? {
      kind: 'element',
      id: graph.selectedNode.id,
      name: graph.selectedNode.name,
      object_type: graph.selectedNode.ontology_class?.name || graph.selectedNode.kind,
    } : graph.selectedRelationship ? {
      kind: 'relationship',
      id: graph.selectedRelationship.id,
      from: graph.selectedRelationship.from.name,
      verb: graph.selectedRelationship.verb,
      to: graph.selectedRelationship.to.name,
    } : undefined,
    result: {
      visible_elements: graph.projection.elements.length,
      visible_relationships: graph.projection.relationships.length,
      total_elements: elements.length,
      total_relationships: relationships.length,
      gap_count: graph.projection.gapCount,
    },
  })

  function openProcess(processMap: ProcessMap) {
    closeContextMenu()
    router.visit(processMaterialPath(projectId, processMap))
  }

  function selectNode(nodeId: string) {
    graph.selectNode(nodeId)
    loadProcessPreview(nodeId)
  }

  function focusNode(nodeId: string) {
    graph.focusNode(nodeId)
    loadProcessPreview(nodeId)
  }

  function loadProcessPreview(nodeId: string) {
    const process = processMaps.find((item) => String(item.element_id) === nodeId)
    if (!process?.element_id || String(loadedProcess?.summary.element_id) === nodeId) return

    const mode = process.modes.find((item) => item.mode === 'reported')?.mode || process.modes[0]?.mode || 'reported'
    router.visit(fdeProjectPath(fdeRoutes.workspace, projectId, {
      view: 'map', process_element_id: String(process.element_id), process_mode: mode,
    }), {
      only: ['selected_process'], preserveScroll: true, preserveState: true, replace: true,
    })
  }

  if (elements.length === 0) return <p className="p-6 text-center text-sm text-muted-foreground">{copy.workspace.no_elements}</p>

  return (
    <div className="flex h-full min-h-0 flex-col bg-background" data-testid="fde-relationship-graph">
      <OntologyGraphToolbar query={graph.query} perspective={graph.perspective} depth={graph.depth} anchors={graph.anchorElements} manualFocus={graph.perspective === 'free'} showGaps={graph.showGaps} gapCount={graph.projection.gapCount} visibleCount={graph.projection.elements.length} totalCount={elements.length} processMaps={processMaps} copy={copy} onQueryChange={graph.setQuery} onPerspectiveChange={graph.choosePerspective} onDepthChange={graph.setDepth} onRemoveAnchor={graph.togglePin} onShowGaps={graph.showGapView} onLeaveGaps={graph.leaveGapView} onSelectProcess={openProcess} />

      <div className="relative min-h-0 flex-1 bg-muted/10" aria-label={copy.workspace.graph_description}>
        {graph.projection.elements.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground">{copy.workspace.graph_empty_filter}</p> : graph.layout.loading ? <div className="flex h-full items-center justify-center"><p className="text-xs text-muted-foreground">{copy.workspace.graph_layouting}</p></div> : (
          <ReactFlow<OntologyFlowNode, OntologyFlowEdge> key={graph.layout.revision} nodes={graph.flowNodes} edges={graph.flowEdges} nodeTypes={nodeTypes} edgeTypes={edgeTypes} fitView fitViewOptions={{ padding: 0.2, maxZoom: 1 }} minZoom={0.25} maxZoom={1.5} nodesDraggable={false} nodesConnectable={false} edgesReconnectable={false} elementsSelectable proOptions={{ hideAttribution: true }} onNodeMouseEnter={(_, node) => graph.hoverNode(node.id)} onNodeMouseLeave={() => graph.hoverNode(null)} onNodeClick={(_, node) => { closeContextMenu(); selectNode(node.id) }} onNodeDoubleClick={(_, node) => { closeContextMenu(); graph.showRelated(node.id) }} onNodeContextMenu={(event, node) => { event.preventDefault(); graph.clearSelection(); setContextMenu({ nodeId: node.id, x: Math.min(event.clientX, window.innerWidth - 220), y: Math.min(event.clientY, window.innerHeight - 230) }) }} onEdgeClick={(_, edge) => { closeContextMenu(); graph.selectRelationship(edge.id) }} onPaneClick={() => { closeContextMenu(); graph.clearSelection() }} className="fde-ontology-flow">
            <Background variant={BackgroundVariant.Dots} gap={22} size={1} color="var(--border)" />
            <Controls showInteractive={false} position="bottom-left" />
          </ReactFlow>
        )}
        <OntologyGraphInspector projectId={projectId} selectedNode={graph.selectedNode} selectedRelationship={graph.selectedRelationship} processMap={selectedProcess} selectedProcess={selectedProcessDetail} dataAssets={dataAssets} dataBindings={dataBindings} evidences={evidences} elements={elements} relationships={relationships} nodeRelationships={graph.selectedNodeRelationships} manuallyPinned={graph.manuallyPinned} pinLimitReached={graph.pinLimitReached} history={history.items} historyLoading={history.loading} historyFailed={history.failed} copy={copy} canManage={canManage} onEdit={onEdit} onEditElement={onEditElement} onOpenType={onOpenType} onOpenProcess={() => { if (selectedProcess) openProcess(selectedProcess) }} onSelectRelationship={graph.selectRelationship} onSelectNode={focusNode} onTogglePin={graph.togglePin} onClear={graph.clearSelection} />
        {contextMenu && contextElement && <OntologyNodeContextMenu element={contextElement} processMap={contextProcess} x={contextMenu.x} y={contextMenu.y} highlightPinned={graph.pinnedHighlightNodeId === contextMenu.nodeId} canManage={canManage} copy={copy} onToggleHighlight={() => { graph.toggleHighlight(contextMenu.nodeId); closeContextMenu() }} onShowRelated={() => { graph.showRelated(contextMenu.nodeId); closeContextMenu() }} onOpenDetails={() => { selectNode(contextMenu.nodeId); closeContextMenu() }} onEdit={() => { onEditElement(contextElement); closeContextMenu() }} onOpenProcess={() => { if (contextProcess) openProcess(contextProcess) }} onClose={closeContextMenu} />}
      </div>
    </div>
  )
}
